import { useCallback, useEffect, useRef, useState } from 'react';

import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import PublicInscriptionStepIllustration, {
  type StepId,
} from './PublicInscriptionStepIllustration';
import '../../styles/publicInscriptionSteps.css';

export type InscriptionStep = {
  n: string;
  stepId: StepId;
  title: string;
  text: string;
};

type Props = {
  steps: InscriptionStep[];
};

export default function PublicInscriptionStepsCarousel({ steps }: Props) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollForward, setCanScrollForward] = useState(false);

  const syncNav = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollBack(el.scrollLeft > 4);
    setCanScrollForward(el.scrollLeft < maxScroll - 4);
  }, []);

  useEffect(() => {
    syncNav();
    const el = trackRef.current;
    if (!el) return undefined;

    el.addEventListener('scroll', syncNav, { passive: true });
    const ro = new ResizeObserver(syncNav);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', syncNav);
      ro.disconnect();
    };
  }, [syncNav, steps.length]);

  const scrollByPage = (direction: -1 | 1) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth, behavior: 'smooth' });
  };

  if (steps.length === 0) return null;

  return (
    <div className="public-inscription-carousel mt-10">
      {steps.length > 1 ? (
        <div className="mb-4 flex justify-end sm:hidden">
          <div className="public-inscription-carousel__nav" aria-label="Navigation du parcours d'inscription">
            <button
              type="button"
              className="public-inscription-carousel__nav-btn"
              onClick={() => scrollByPage(-1)}
              disabled={!canScrollBack}
              aria-label="Étape précédente"
            >
              <LucideSymbolIcon name="arrow_back" tone="inherit" size={20} />
            </button>
            <button
              type="button"
              className="public-inscription-carousel__nav-btn"
              onClick={() => scrollByPage(1)}
              disabled={!canScrollForward}
              aria-label="Étape suivante"
            >
              <LucideSymbolIcon name="arrow_forward" tone="inherit" size={20} />
            </button>
          </div>
        </div>
      ) : null}

      <div
        ref={trackRef}
        className="public-inscription-carousel__track"
        role="list"
        aria-label="Étapes pour s'inscrire"
      >
        {steps.map((step) => (
          <article key={step.n} className="public-inscription-carousel__slide" role="listitem">
            <div className="public-inscription-step-card public-hover-lift h-full">
              <PublicInscriptionStepIllustration step={step.stepId} />
              <div className="public-inscription-step-card__body">
                <span className="public-inscription-step-card__number">Étape {step.n}</span>
                <h3 className="public-inscription-step-card__title">{step.title}</h3>
                <p className="public-inscription-step-card__text">{step.text}</p>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
